import getCompanyApi from '@/api/get/company/get-company';
import CRUDPageWrapper from '@/components/crud-page-wrapper/crud-page-wrapper';
import ErrorComponent from '@/components/error/error-component';
import PasswordEdit from '@/pages/account/password-edit';
import ProfileEdit from '@/pages/account/profile-edit';
import ProfileManagement from '@/pages/account/profile-management';
import CategoriesAll from '@/pages/categories/categories-all';
import CompaniesAll from '@/pages/company-management/companies-all';
import CompanyEdit from '@/pages/company-management/company-edit';
import CompanyManagement from '@/pages/company-management/company-management';
import CompanyNew from '@/pages/company-management/company-new';
import OptionView from '@/pages/company-management/company-options/option-view';
import OptionsAll from '@/pages/company-management/company-options/options-all';
import OptionsEdit from '@/pages/company-management/company-options/options-edit';
import OptionsNew from '@/pages/company-management/company-options/options-new';
import CompanyPricingAll from '@/pages/company-management/company-pricing/pricing-all';
import ProductsEdit from '@/pages/company-management/company-products/product-edit';
import ProductNew from '@/pages/company-management/company-products/product-new';
import ProductsAll from '@/pages/company-management/company-products/products-all';
import ProductPreview from '@/pages/company-management/company-products/product-preview';
import QuoteModule from '@/pages/company-management/company-quote-module/quote-module';
import CompanyView from '@/pages/company-management/company-view';
import ProductCategoriesAll from '@/pages/company-management/product-categories/product-categories-all';
import Dashboard from '@/pages/dashboard/dashboard';
import UserManagement from '@/pages/user-management/user-management';
import UserRoleAll from '@/pages/user-management/user-roles/user-roles-all';
import UserRolesEdit from '@/pages/user-management/user-roles/user-roles-edit';
import UserRolesNew from '@/pages/user-management/user-roles/user-roles-new';
import UserRolesView from '@/pages/user-management/user-roles/user-roles-view';
import UserAll from '@/pages/user-management/user/user-all';
import UserEdit from '@/pages/user-management/user/user-edit';
import UserNew from '@/pages/user-management/user/user-new';
import UserView from '@/pages/user-management/user/user-view';
import { FiHome, FiUser } from 'react-icons/fi';
import { MdOutlineAssignmentInd, MdOutlineDashboard } from 'react-icons/md';
import { Link, Navigate } from 'react-router-dom';
import TemplatesAll from '@/pages/company-management/company-templates/templates-all';
import TemplateEdit from '@/pages/company-management/company-templates/template-edit';
import TemplateNew from '@/pages/company-management/company-templates/template-new';
import TemplatePreview from '@/pages/company-management/company-templates/template-preview';
import RenderCompanyName from './components/render-company-name';

const AuthRoutes = [
  {
    name: 'dashboard',
    path: '',
    element: <Navigate to="dashboard" />,
    errorElement: <ErrorComponent />,
  },
  {
    name: 'dashboard',
    path: 'dashboard',
    sidebar: true,
    sidebarIcon: FiHome,
    element: <Dashboard />,
    errorElement: <ErrorComponent />,
  },
  {
    path: 'profile-management',
    id: 'profile-management',
    errorElement: <ErrorComponent />,
    satisfyAll: false,
    children: [
      {
        index: true,
        element: <Navigate to="profile" />,
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <ProfileManagement />,
            children: [
              {
                index: true,
                element: <ProfileEdit />,
              },
              {
                index: true,
                path: 'change-password',
                element: <PasswordEdit />,
                id: 'profile-password',
                satisfyAll: true,
                errorElement: <ErrorComponent />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'user-management',
    id: 'user-management',
    name: 'User Management',
    active: 'userManagement',
    sidebar: true,
    sidebarIcon: FiUser,
    errorElement: <ErrorComponent />,
    element: <UserManagement />,
    permissions: ['user.read', 'userRole.read'],
    satisfyAll: false,
    handle: {
      crumb: () => <span>User Management</span>,
    },
    children: [
      {
        index: true,
        permissions: ['user.read'],
        element: <Navigate to="users" />,
      },
      {
        fallbackIndex: true,
        permissions: ['userRole.read'],
        element: <Navigate to="user-roles" />,
      },
      {
        title: 'Users',
        path: 'users',
        element: <UserAll />,
        tab: true,
        permissions: ['user.read'],
      },
      {
        title: 'User Roles',
        path: 'user-roles',
        tab: true,
        element: <UserRoleAll />,
        permissions: ['userRole.read'],
      },
    ],
  },
  {
    path: 'user-management/users',
    id: 'user-management-user-crud',
    element: <CRUDPageWrapper />,
    errorElement: <ErrorComponent />,
    permissions: ['user.read', 'user.create', 'user.update', 'user.delete'],
    satisfyAll: false,
    handle: {
      crumb: () => <Link to="/app/user-management">User Management</Link>,
    },
    children: [
      {
        path: 'new',
        element: <UserNew />,
        permissions: ['user.create'],
        satisfyAll: true,
        errorElement: <ErrorComponent />,
        handle: {
          crumb: () => <span>New User</span>,
        },
      },
      {
        path: 'edit/:id',
        element: <UserEdit />,
        permissions: ['user.update'],
        satisfyAll: true,
        errorElement: <ErrorComponent />,
        handle: {
          crumb: () => <span>Edit User</span>,
        },
      },
      {
        path: 'view/:id',
        element: <UserView />,
        errorElement: <ErrorComponent />,
        handle: {
          crumb: () => <span>View User</span>,
        },
        permissions: ['user.read'],
      },
    ],
  },
  {
    path: 'user-management/user-roles',
    id: 'user-management-user-roles-crud',
    element: <CRUDPageWrapper />,
    errorElement: <ErrorComponent />,
    permissions: [
      'userRole.read',
      'userRole.create',
      'userRole.update',
      'userRole.delete',
    ],
    satisfyAll: false,
    handle: {
      crumb: () => <Link to="/app/user-management">User Management</Link>,
    },
    children: [
      {
        path: 'new',
        element: <UserRolesNew />,
        permissions: ['userRole.create'],
        satisfyAll: true,
        errorElement: <ErrorComponent />,
        handle: {
          crumb: () => <span>New User Role</span>,
        },
      },
      {
        path: 'edit/:id',
        element: <UserRolesEdit />,
        permissions: ['userRole.update'],
        satisfyAll: true,
        errorElement: <ErrorComponent />,
        handle: {
          crumb: () => <span>Edit User Role</span>,
        },
      },
      {
        path: 'view/:id',
        element: <UserRolesView />,
        permissions: ['userRole.read'],
        satisfyAll: true,
        errorElement: <ErrorComponent />,
        handle: { crumb: () => <span>View User Role</span> },
      },
    ],
  },
  {
    name: 'Company Management',
    path: 'company-management',
    id: 'companies',
    sidebar: true,
    permissions: ['company.read'],
    sidebarIcon: MdOutlineAssignmentInd,
    active: 'companyManagement',
    errorElement: <ErrorComponent />,
    handle: {
      crumb: () => <Link to="/app/company-management">Company Management</Link>,
    },
    children: [
      {
        path: '',
        id: 'company-all',
        permissions: ['company.read'],
        element: <CompaniesAll />,
        errorElement: <ErrorComponent />,
      },
      {
        name: 'Company Management',
        path: 'view/:id',
        id: 'companies-crud',
        element: <CRUDPageWrapper fullWidth />,
        permissions: ['company.read'],
        active: 'companyManagement',
        loader: async ({ params }) => getCompanyApi(params.id),
        handle: {
          parent: ({ id }) => <RenderCompanyName id={id} />,
        },
        errorElement: <ErrorComponent />,
        children: [
          {
            element: <CompanyManagement />,
            children: [
              {
                path: '',
                index: true,
                element: <CompanyView />,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Details</span>,
                },
              },
              {
                path: 'options',
                element: <OptionsAll />,
                id: 'option-all',
                permissions: ['option.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Options</span>,
                },
              },
              {
                path: 'pricing',
                element: <CompanyPricingAll />,
                id: 'pricing-all',
                permissions: ['price.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Pricing</span>,
                },
              },
              {
                path: 'templates',
                element: <TemplatesAll />,
                id: 'templates-all',
                permissions: ['template.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Templates</span>,
                },
              },
              {
                path: 'products',
                element: <ProductsAll />,
                id: 'products-all',
                permissions: ['product.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Products</span>,
                },
              },
              {
                path: 'quote-module',
                element: <QuoteModule />,
                id: 'quoteModule',
                permissions: ['quoteModule.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Quote Module</span>,
                },
              },
              {
                name: 'Product Category',
                path: 'product-category',
                title: 'Product Category',
                id: 'product-category',
                tab: true,
                active: 'companyManagement',
                permissions: ['productCategory.read'],
                satisfyAll: true,
                element: <ProductCategoriesAll />,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Product Category</span>,
                },
              },
            ],
          },
          {
            handle: {
              parent: ({ id }) => (
                <Link to={`/app/company-management/view/${id}/options`}>
                  Options{' '}
                </Link>
              ),
            },
            children: [
              {
                path: 'options/new',
                element: <OptionsNew />,
                id: 'options-new',
                permissions: ['option.create'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Create New Option</span>,
                },
              },
              {
                path: 'options/edit/:optionId',
                element: <OptionsEdit />,
                id: 'options-edit',
                permissions: ['option.update'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Edit Option</span>,
                },
              },
              {
                path: 'options/view/:optionId',
                element: <OptionView />,
                id: 'option-view',
                permissions: ['option.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>View Option</span>,
                },
              },
            ],
          },
          {
            handle: {
              parent: ({ id }) => (
                <Link to={`/app/company-management/view/${id}/products`}>
                  Products{' '}
                </Link>
              ),
            },
            children: [
              {
                path: 'products/new',
                element: <ProductNew />,
                id: 'products-view',
                permissions: ['product.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>New Products</span>,
                },
              },
              {
                path: 'products/edit/:productId',
                element: <ProductsEdit />,
                id: 'products-edit',
                permissions: ['product.update'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Edit Product</span>,
                },
              },
              {
                path: 'products/edit/:productId/preview',
                element: <ProductPreview />,
                id: 'products-preview',
                permissions: ['product.update'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Preview Product</span>,
                },
              },
            ],
          },
          {
            handle: {
              parent: ({ id }) => (
                <Link to={`/app/company-management/view/${id}/templates`}>
                  Templates{' '}
                </Link>
              ),
            },
            children: [
              {
                path: 'templates/new',
                element: <TemplateNew />,
                id: 'templates-view',
                permissions: ['template.read'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>New Templates</span>,
                },
              },
              {
                path: 'templates/edit/:templateId',
                element: <TemplateEdit />,
                id: 'templates-edit',
                permissions: ['template.update'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Edit Template</span>,
                },
              },
              {
                path: 'templates/edit/:templateId/preview',
                element: <TemplatePreview />,
                id: 'templates-preview',
                permissions: ['template.update'],
                satisfyAll: true,
                errorElement: <ErrorComponent />,
                handle: {
                  crumb: () => <span>Preview Template</span>,
                },
              },
            ],
          },
        ],
      },
      {
        path: '',
        id: 'company-new',
        element: <CRUDPageWrapper />,
        permissions: ['company.create', 'company.update'],
        satisfyAll: false,
        errorElement: <ErrorComponent />,
        children: [
          {
            path: 'new',
            id: 'company-create',
            permissions: ['company.create'],
            element: <CompanyNew />,
            errorElement: <ErrorComponent />,
            handle: {
              crumb: () => <span>New Company</span>,
            },
          },
          {
            path: 'edit/:id',
            id: 'company-edit',
            element: <CompanyEdit />,
            permissions: ['company.update'],
            satisfyAll: true,
            errorElement: <ErrorComponent />,
            handle: {
              crumb: () => <span>Edit Company</span>,
            },
          },
        ],
      },
    ],
  },
  {
    name: 'Global Categories',
    path: 'categories',
    id: 'categories',
    sidebar: true,
    permissions: ['category.read'],
    sidebarIcon: MdOutlineDashboard,
    active: 'categories',
    element: <CategoriesAll />,
    errorElement: <ErrorComponent />,
  },

  {
    path: '*',
    element: <Navigate to="dashboard" />,
  },
];

export default AuthRoutes;
